import {
	useCallback, useMemo, useState, useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GRS } from '../stores/types/types';
import type { Shot } from '../stores/types/shots';
import { baseShot } from '../stores/reducers/shots';
import { VideoStream } from '../components/types/videos';
import type { MediaItem } from '../stores/types/media';
import { isVideo } from '../utils/utils';
import { ResourcePoolContext } from './use-resource-pool';
import { getVideoDuration } from '../utils/media';
import { MODAL_TEMPLATES } from '../stores/reducers/modal';

const useShotMemos = () => {
    const currentShotIdEntity = useSelector((s: GRS) => s.ShotsReducer.currentShotIdEntity);
    const shots = useSelector((s: GRS) => s.ShotsReducer.shots);
    const [backupShot, setBackupShot] = useState<Shot>(shots ? shots[0] : baseShot);
    const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
    const user = useSelector((s: GRS) => s.SessionReducer.user);
    const connectedMediaVideos = useSelector((s: GRS) => s.VideoReducer.connectedMediaVideos);
    const connectedPresenterVideos = useSelector((s: GRS) => s.VideoReducer.connectedPresenterVideos);
    const screenShareWebrtcId = useSelector((s: GRS) => s.SessionReducer.screenShareWebrtcId);
    const media = useSelector((s: GRS) => s.MediaReducer.media);
    const modalOpen = useSelector((s: GRS) => s.ModalReducer.modalOpen);
    const modalTemplate = useSelector((s: GRS) => s.ModalReducer.modalTemplate);
    const resourcePool = useContext(ResourcePoolContext);

    const currentShot = useMemo(() => {
		const currId: number | undefined = currentShotIdEntity?.entityValue?.currentShotId;
		let returnedShot = shots.find((shot: Shot) => shot.shot_id === currId);

		// eslint-disable-next-line prefer-destructuring
		if (!returnedShot) { returnedShot = shots[0]; }
		if (!returnedShot) { returnedShot = backupShot; }
		if (!returnedShot) { returnedShot = baseShot; }

		setBackupShot(returnedShot);

		return returnedShot;
	}, [currentShotIdEntity, shots, backupShot]);

    const nextShot = useMemo(() => {
		const nextShotIndex = shots.find(
			(shot: Shot) => (shot.shot_data.order) === (currentShot.shot_data.order + 1),
		);
		return nextShotIndex;
	}, [currentShotIdEntity, shots, backupShot]);

    const isShotBuilderOpen = useMemo((): boolean => {
		if (modalOpen && modalTemplate === MODAL_TEMPLATES.SHOT_BUILDER) {
			return true;
		}

		return false;
	}, [modalOpen, modalTemplate]);

    const idsOfVideosInCanvas = useMemo(() => {
		const newIdsOfVideosInCanvas = [] as string[];

		currentShot.shot_data.shotFeeds.forEach((feed) => {
			if (feed.media) {
				const connectedVideoForMedia = connectedMediaVideos.find(
					(vid: VideoStream) => vid.mediaId === feed.media,
				);
				if (connectedVideoForMedia) { newIdsOfVideosInCanvas.push(connectedVideoForMedia.id); }
			}

			if (feed.presenter) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.profile === feed.presenter,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				}
			}

			if (feed.screenshare) {
				const connectedUser = connectedUsers
					.find((_connectedUser) => _connectedUser.email === feed.screenshare);

				if (connectedUser) {
					const connectedVideoForPresenter = connectedPresenterVideos.find(
						(vid: VideoStream) => vid.id === connectedUser.screenShareWebrtcId,
					);

					if (connectedVideoForPresenter) {
						newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
					}
				}
			}

			if (feed.sipStreamId) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.id === feed.sipStreamId,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				}
			}
		});

		return newIdsOfVideosInCanvas;
	}, [connectedPresenterVideos, connectedMediaVideos, currentShot.shot_data.shotFeeds]);

    const connectedUser = useMemo(() => (
		connectedUsers
			.find((_connectedUser) => user?.gr_user && _connectedUser.gr_user === user.gr_user)
	), [user?.gr_user, connectedUsers]);

    // screenshare isn't considered on canvas
	const isOnCanvas = useMemo(() => {
		let found = false;

		if (connectedUser?.feedWebRTCIds && connectedUser.feedWebRTCIds.length > 0) {
			const ids = idsOfVideosInCanvas.filter((id) => id !== screenShareWebrtcId);

			for (let i = 0; i < ids.length; i += 1) {
				if (connectedUser.feedWebRTCIds?.includes(ids[i])) {
					found = true;
					break;
				}
			}
		}

		return found;
	}, [idsOfVideosInCanvas, connectedUser, screenShareWebrtcId]);

    const getShotDuration = useCallback(async (mediaId: number | null, oldDuration: number = 0) => {
		const mediaItem = media.find((med: MediaItem) => med.id === mediaId);

		if (mediaItem) {
			const pages = mediaItem.media_data.image_array;
			let suggestedDuration = 0;
			const video = document.createElement('video');
			video.preload = 'metadata';
			video.crossOrigin = 'anonymous';
			for (let i = 0; i < pages.length; i += 1) {
				const page = pages[i];
				if (isVideo(page) && !!resourcePool) {
					// eslint-disable-next-line no-await-in-loop
					const videoDuration = await getVideoDuration(page, mediaItem.type, video, resourcePool);
					suggestedDuration += videoDuration;
				}
			}

			if (suggestedDuration) {
				// max duration is 99 minutes
				return suggestedDuration;
			}
			return oldDuration;
		}
		return oldDuration;
	}, [media, resourcePool]);
    
    return {
        currentShot,
        nextShot,
        isOnCanvas,
        isShotBuilderOpen,
        getShotDuration,
    };
}
export default useShotMemos;